/**
 * Low-Confidence Answers — admin view powered by Phase D telemetry.
 *
 * Reads from `chatbot_low_confidence_queries` (D1) via
 * GET /chatbot/low-confidence. Shows the admin what visitors are asking
 * that the bot can't answer well, with one-click "mark reviewed" so the
 * queue actually drains as they fix things (add FAQ, fill business
 * profile, re-train sources).
 *
 * Why this is its own file (and not inlined into Chatbot.tsx):
 *   - The component owns its own queries + mutations; mounting it in
 *     more than one place (Overview, future weekly digest email, etc.)
 *     stays a one-import change.
 *   - Keeps the already-1500-LOC Chatbot.tsx from sprawling further.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, AlertCircle, CheckCircle2, MessageCircleQuestion, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface LowConfRow {
  id: number;
  conversation_id: string | null;
  message: string;
  intent: 'location' | 'contact' | 'hours' | 'pricing' | null;
  max_similarity: number | null;
  had_business_profile: number;
  forced_refusal: number;
  created_at: string;
  reviewed: number;
  reviewed_at: string | null;
  reviewed_note: string | null;
}

interface LowConfStats {
  total?: number;
  unreviewed?: number;
  unreviewed_location?: number;
  unreviewed_contact?: number;
  unreviewed_hours?: number;
  unreviewed_pricing?: number;
}

const INTENT_LABEL: Record<NonNullable<LowConfRow['intent']>, string> = {
  location: 'Location',
  contact: 'Contact',
  hours: 'Hours',
  pricing: 'Pricing',
};

const INTENT_ADVICE: Record<NonNullable<LowConfRow['intent']>, string> = {
  location: 'Fix: fill out the address in the Business info tab.',
  contact: 'Fix: add a phone / email to the Business info tab.',
  hours: 'Fix: add operating hours to the Business info tab.',
  pricing: 'Fix: add a pricing FAQ or product to the knowledge base.',
};

export function LowConfidenceList({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['low-confidence', projectId, includeReviewed],
    queryFn: async () => {
      const res = await api.get<{
        success: boolean;
        data: { rows: LowConfRow[]; stats: LowConfStats };
      }>(
        `/projects/${projectId}/chatbot/low-confidence${includeReviewed ? '?includeReviewed=1' : ''}`
      );
      return res.data?.data;
    },
    refetchInterval: 60_000,
  });

  const review = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/projects/${projectId}/chatbot/low-confidence/${id}/review`, {});
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['low-confidence', projectId] });
      toast.success('Marked reviewed.');
    },
    onError: () => toast.error('Could not mark reviewed.'),
  });

  const rows = data?.rows ?? [];
  const stats = data?.stats ?? {};

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageCircleQuestion className="h-5 w-5" />
              Low-confidence answers
            </CardTitle>
            <CardDescription>
              Questions in the last 30 days the bot couldn&rsquo;t answer with confidence. Each row
              is a chance to make the bot better — usually by adding a FAQ, filling out the Business
              info, or re-training a knowledge source.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIncludeReviewed((v) => !v)}
          >
            {includeReviewed ? 'Hide reviewed' : 'Show reviewed'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary chips */}
        <div className="flex flex-wrap gap-2">
          <Stat label="Unreviewed" value={stats.unreviewed ?? 0} tone="amber" />
          <Stat label="Location" value={stats.unreviewed_location ?? 0} />
          <Stat label="Contact" value={stats.unreviewed_contact ?? 0} />
          <Stat label="Hours" value={stats.unreviewed_hours ?? 0} />
          <Stat label="Pricing" value={stats.unreviewed_pricing ?? 0} />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <p>No low-confidence answers in the last 30 days. Nice.</p>
          </div>
        ) : (
          <div className="divide-y rounded-md border">
            {rows.map((row) => (
              <Row key={row.id} row={row} onReview={() => review.mutate(row.id)} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'amber' }) {
  const cls =
    tone === 'amber' && value > 0
      ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`rounded-md px-3 py-1 text-xs ${cls}`}>
      {label}: <strong>{value}</strong>
    </span>
  );
}

function Row({ row, onReview }: { row: LowConfRow; onReview: () => void }) {
  const ageLabel = (() => {
    try {
      return formatDistanceToNow(new Date(`${row.created_at}Z`), { addSuffix: true });
    } catch {
      return row.created_at;
    }
  })();

  return (
    <div className={`p-3 ${row.reviewed ? 'opacity-60' : ''}`}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        {row.intent && (
          <Badge variant="secondary" className="text-xs">
            {INTENT_LABEL[row.intent]}
          </Badge>
        )}
        {row.forced_refusal === 1 && (
          <Badge variant="outline" className="border-amber-500 text-xs text-amber-700">
            <AlertCircle className="mr-1 h-3 w-3" />
            Refused (no source)
          </Badge>
        )}
        {row.max_similarity !== null && (
          <span className="text-muted-foreground text-xs">
            Best match: {(row.max_similarity * 100).toFixed(0)}%
          </span>
        )}
        <span className="text-muted-foreground ml-auto text-xs">{ageLabel}</span>
      </div>
      <p className="text-sm">&ldquo;{row.message}&rdquo;</p>
      {row.intent && !row.reviewed && (
        <p className="text-muted-foreground mt-1 text-xs italic">{INTENT_ADVICE[row.intent]}</p>
      )}
      {row.reviewed === 0 ? (
        <div className="mt-2 flex justify-end">
          <Button size="sm" variant="outline" onClick={onReview}>
            Mark reviewed
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground mt-1 text-xs">
          Reviewed{' '}
          {row.reviewed_at
            ? formatDistanceToNow(new Date(`${row.reviewed_at}Z`), { addSuffix: true })
            : ''}
          {row.reviewed_note ? ` — ${row.reviewed_note}` : ''}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Freshness banner — manual "Check for changes" button for URL sources.
// Pure manual per the agreed Phase D scope (no auto re-crawl cron).
// ─────────────────────────────────────────────────────────────────────────

interface FreshnessRow {
  sourceId: string;
  url: string;
  name: string;
  lastProcessedAt: string | null;
  status: 'fresh' | 'changed' | 'unknown' | 'error';
  reason?: string;
}

export function FreshnessBanner({ projectId }: { projectId: string }) {
  const [result, setResult] = useState<{
    sources: FreshnessRow[];
    checked: number;
    changed: number;
  } | null>(null);

  const check = useMutation({
    mutationFn: async () => {
      const res = await api.get<{
        success: boolean;
        data: { sources: FreshnessRow[]; checked: number; changed: number };
      }>(`/projects/${projectId}/chatbot/sources/freshness`);
      return res.data?.data;
    },
    onSuccess: (data) => {
      if (data) setResult(data);
    },
    onError: () => toast.error('Freshness check failed'),
  });

  return (
    <div className="mb-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">
          Check whether the live URL content has changed since the last training run. No re-crawl,
          no embedding cost — just a quick check.
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => check.mutate()}
          disabled={check.isPending}
        >
          {check.isPending ? (
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-3 w-3" />
          )}
          Check for changes
        </Button>
      </div>

      {result && result.checked === 0 && (
        <div className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-xs">
          No URL sources to check.
        </div>
      )}

      {result && result.changed > 0 && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-900/20">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Website content has changed for {result.changed} source
            {result.changed === 1 ? '' : 's'} — re-train recommended.
          </p>
          <ul className="space-y-1 text-xs text-amber-800 dark:text-amber-200">
            {result.sources
              .filter((s) => s.status === 'changed')
              .map((s) => (
                <li key={s.sourceId}>
                  <strong>{s.name}</strong> — {s.url}
                  {s.reason && (
                    <span className="text-amber-700 dark:text-amber-300"> ({s.reason})</span>
                  )}
                </li>
              ))}
          </ul>
        </div>
      )}

      {result && result.changed === 0 && result.checked > 0 && (
        <div className="text-muted-foreground flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-xs text-green-800 dark:bg-green-900/20 dark:text-green-200">
          <CheckCircle2 className="h-3 w-3" />
          All {result.checked} URL source{result.checked === 1 ? '' : 's'} appear fresh.
          {result.sources.some((s) => s.status === 'unknown') &&
            " (Some servers don't expose Last-Modified — re-train if you suspect drift.)"}
        </div>
      )}
    </div>
  );
}
